/**
 * The Quality Management module's route table (C7 §4).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `QMS_ROUTES` in. Keeping them here means the
 * paths, the permissions and the components that implement them are decided in
 * one place by whoever owns the module.
 *
 * Every `:id` segment carries the `\d+` constraint, so no literal word can ever
 * be parsed as a document id, and the list route of each family is declared
 * before its detail route so the ordering says the same thing the constraint
 * enforces.
 *
 * Which permission guards which screen follows the API rather than the topic.
 * The recording sheet is `qms.inspection.read` because that is what
 * `GET /inspection-lots/:id` declares — *recording* on it needs
 * `qms.inspection.record`, and the screen checks that separately, because an
 * inspector and a reader are different people and the whole module is built on
 * that distinction.
 */

import type { RouteRecordRaw } from 'vue-router'

import { QMS_PERMISSIONS } from '@/modules/qms/permissions'

export const QMS_ROUTES: RouteRecordRaw[] = [
  {
    path: '/qms/inspection-plans',
    name: 'qms-inspection-plans',
    component: () => import('@/modules/qms/InspectionPlansListView.vue'),
    meta: { titleKey: 'qms.routeTitles.plans', permission: QMS_PERMISSIONS.planRead },
  },
  {
    path: '/qms/inspection-plans/:id(\\d+)',
    name: 'qms-inspection-plan',
    component: () => import('@/modules/qms/InspectionPlanView.vue'),
    meta: { titleKey: 'qms.routeTitles.plan', permission: QMS_PERMISSIONS.planRead },
  },
  {
    path: '/qms/sampling-plans',
    name: 'qms-sampling-plans',
    component: () => import('@/modules/qms/SamplingPlansListView.vue'),
    meta: { titleKey: 'qms.routeTitles.samplingPlans', permission: QMS_PERMISSIONS.planRead },
  },
  {
    path: '/qms/inspections',
    name: 'qms-inspections',
    component: () => import('@/modules/qms/InspectionWorklistView.vue'),
    meta: { titleKey: 'qms.routeTitles.worklist', permission: QMS_PERMISSIONS.inspectionRead },
  },
  {
    path: '/qms/inspections/:id(\\d+)',
    name: 'qms-inspection',
    component: () => import('@/modules/qms/InspectionView.vue'),
    meta: { titleKey: 'qms.routeTitles.inspection', permission: QMS_PERMISSIONS.inspectionRead },
  },
  {
    path: '/qms/capa',
    name: 'qms-capa-board',
    component: () => import('@/modules/qms/CapaBoardView.vue'),
    meta: { titleKey: 'qms.routeTitles.capaBoard', permission: QMS_PERMISSIONS.capaRead },
  },
  {
    path: '/qms/capa/:id(\\d+)',
    name: 'qms-capa',
    component: () => import('@/modules/qms/CapaView.vue'),
    meta: { titleKey: 'qms.routeTitles.capa', permission: QMS_PERMISSIONS.capaRead },
  },
  {
    path: '/qms/reports/quality-summary',
    name: 'qms-quality-summary',
    component: () => import('@/modules/qms/QualitySummaryView.vue'),
    meta: {
      titleKey: 'qms.routeTitles.qualitySummary',
      permission: QMS_PERMISSIONS.inspectionRead,
    },
  },
  {
    path: '/qms/settings',
    name: 'qms-settings',
    component: () => import('@/modules/qms/QmsSettingsView.vue'),
    meta: { titleKey: 'qms.routeTitles.settings', permission: QMS_PERMISSIONS.planRead },
  },
]

/** The same set as strings, for links inside the module. */
export const QMS_PATHS = {
  plans: '/qms/inspection-plans',
  plan: (id: number) => `/qms/inspection-plans/${id}`,
  samplingPlans: '/qms/sampling-plans',
  worklist: '/qms/inspections',
  inspection: (id: number) => `/qms/inspections/${id}`,
  capaBoard: '/qms/capa',
  capa: (id: number) => `/qms/capa/${id}`,
  qualitySummary: '/qms/reports/quality-summary',
  settings: '/qms/settings',
} as const
